import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck, CloudLightning } from "lucide-react";
import { PageHero } from "@/components/Bits";
import { Article } from "@/components/ArticleBody";
import { TeenShareCard } from "@/components/TeenShareCard";
import { TEEN_FACTS } from "@/lib/articles";
import { TEEN_FACTS_ES } from "@/lib/articles.es";
import { useLang, LangToggle } from "@/lib/lang";
import { T } from "@/lib/i18n";

const S = {
  en: {
    eyebrow: "Super Kids · Real Talk for Teens", title: "Real talk about depression",
    sub: "Simple facts, honest signs, and what to do if you or a friend is having a hard time.",
    swEyebrow: "Join the teen connection", swTitle: "Storm Watchers",
    swSub: "For the ones who've weathered storms and look out for each other. Join the crew, get a first look at the Storm Watchers collection: cool stuff for the chillest.",
    swCta: "Join Storm Watchers", back: "Back to Super Kids",
  },
  es: {
    eyebrow: "Super Kids · Hablemos claro, para jóvenes", title: "Hablemos claro sobre la depresión",
    sub: "Datos sencillos, señales honestas y qué hacer si tú o un amigo la están pasando mal.",
    swEyebrow: "Únete a la conexión juvenil", swTitle: "Storm Watchers",
    swSub: "Para quienes han pasado tormentas y se cuidan entre sí. Únete al grupo y conoce primero la colección Storm Watchers: cosas cool para los más tranquilos.",
    swCta: "Únete a Storm Watchers", back: "Volver a Super Kids",
  },
};

const Disclaimer = ({ text, testid }) => (
  <p className="inline-flex items-start gap-2.5 text-left text-base sm:text-lg font-semibold text-bsn-champagne bg-bsn-navy border-2 border-bsn-gold rounded-2xl px-5 py-3.5 max-w-2xl" data-testid={testid}>
    <ShieldCheck className="w-6 h-6 text-bsn-gold shrink-0 mt-0.5" />{text}
  </p>
);

const StormPromo = ({ s }) => (
  <Link to="/storm-watchers" className="mt-14 group block rounded-3xl overflow-hidden bg-bsn-midnight text-bsn-parchment card-tactile" data-testid="teen-storm-watchers-promo">
    <div className="grid grid-cols-4 h-40 sm:h-56">
      {[1, 2, 3, 4].map((n) => <img key={n} src={`/teen/look_${n}.jpg`} alt="" className="w-full h-full object-cover object-top" />)}
    </div>
    <div className="p-7 sm:p-10">
      <p className="eyebrow text-bsn-gold mb-2 inline-flex items-center gap-2"><CloudLightning className="w-4 h-4" /> {s.swEyebrow}</p>
      <h2 className="font-serif text-3xl sm:text-4xl font-semibold">{s.swTitle}</h2>
      <p className="mt-3 text-bsn-ice/80 max-w-2xl">{s.swSub}</p>
      <span className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold">{s.swCta} <ArrowRight className="w-4 h-4" /></span>
    </div>
  </Link>
);

export default function TeenFacts() {
  const { lang } = useLang();
  const s = S[lang];
  const t = T[lang];
  return (
    <div data-testid="teen-facts-page" data-i18n="manual">
      <PageHero
        eyebrow={s.eyebrow}
        title={s.title}
        subtitle={s.sub}
        image="/teen/hero.jpg"
        imageClass="opacity-90"
        overlayClass="from-bsn-midnight/40 via-bsn-midnight/30 to-bsn-midnight/90"
        testid="teen-facts-hero"
      >
        <div className="flex flex-col items-start gap-4">
          <LangToggle />
          <Disclaimer text={t.disclaimer} testid="teen-disclaimer-top" />
        </div>
      </PageHero>
      <section className="max-w-5xl mx-auto px-4 -mt-10 pb-20 relative">
        <Article article={lang === "es" ? TEEN_FACTS_ES : TEEN_FACTS} eyebrow={t.forTeens} path="/teen-facts" />
        <TeenShareCard />
        <div className="mt-6 text-center"><Disclaimer text={t.disclaimer} testid="teen-disclaimer-downloads" /></div>
        <StormPromo s={s} />
        <Link to="/super-kids" className="mt-8 inline-flex items-center gap-2 text-bsn-bronze font-semibold hover:text-bsn-midnight" data-testid="teen-back-to-superkids">
          <ArrowLeft className="w-4 h-4" /> {s.back}
        </Link>
      </section>
    </div>
  );
}
